import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ShortTextIcon from '@mui/icons-material/ShortText';
import ArticleIcon from '@mui/icons-material/Article';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import {
  EXERCISE_TYPES,
  CURRICULUM,
  resolveTargetWpm,
  resolveGoalTierLabel,
} from './typingTutorCurriculum';
import { fetchTypingProgress } from './typingTutorApi';
import { requireStudentAuth, parseStudentData } from './typingTutorUtils';
import './typingTutorTheme.css';

const ICON_MAP = {
  keyboard: KeyboardIcon,
  text_fields: TextFieldsIcon,
  short_text: ShortTextIcon,
  article: ArticleIcon,
  menu_book: MenuBookIcon,
};

export default function TypingTutorHome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const student = parseStudentData();
  const targetWpm = progress?.targetWpm ?? resolveTargetWpm(student?.enrolled_courses);
  const goalLabel = resolveGoalTierLabel(student?.enrolled_courses);

  useEffect(() => {
    if (!requireStudentAuth(navigate)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchTypingProgress();
        if (!cancelled) setProgress(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load progress. You can still practice.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const completedSet = new Set(
    (progress?.completedLessonKeys || progress?.completedLessons || []).map(
      (l) => `${l.exerciseType}:${l.lessonId}`,
    ),
  );

  const isLessonDone = (typeId, lessonId) => completedSet.has(`${typeId}:${lessonId}`);

  return (
    <Box className="tt-root">
      <Box className="tt-header">
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate('/student/track-sheet')} sx={{ color: '#fff' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                KNLS Typing Tutor
              </Typography>
              <Typography variant="body2" className="tt-sans" sx={{ opacity: 0.9 }}>
                Classical practice — fingering to stories. Records sync to My Journey app.
              </Typography>
            </Box>
            <span className={`tt-goal-badge ${progress?.goalMet ? 'tt-goal-met' : ''}`}>
              Goal: {targetWpm} WPM
              {progress?.goalTier === 'higher' || targetWpm >= 55 ? ` (${goalLabel})` : ' (DCA)'}
            </span>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} className="tt-sans">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress color="success" />
          </Box>
        ) : (
          <>
            {progress && (
              <>
              <Box className="tt-card" sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Your Progress
                </Typography>
                <Box className="tt-sans" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                  <Typography variant="body2">
                    Best: <strong>{Math.round(progress.bestWpm)} WPM</strong>
                  </Typography>
                  <Typography variant="body2">
                    Avg accuracy: <strong>{progress.avgAccuracy}%</strong>
                  </Typography>
                  <Typography variant="body2">
                    Sessions: <strong>{progress.totalSessions}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Practice time: <strong>{Math.round(progress.totalTimeSeconds / 60)} min</strong>
                  </Typography>
                </Box>
                <div className="tt-progress-track">
                  <div
                    className="tt-progress-fill"
                    style={{ width: `${progress.goalProgress || 0}%` }}
                  />
                </div>
                <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)', mt: 0.5, display: 'block' }}>
                  {progress.goalProgress}% toward {targetWpm} WPM goal
                </Typography>
              </Box>

              {progress.certificate && (
                <Box className="tt-card" sx={{ p: 2.5, mb: 3, border: '2px solid var(--tt-accent)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700}>
                      TT Speed Certificate
                    </Typography>
                    <span
                      className={`tt-goal-badge ${progress.certificate.earned ? 'tt-goal-met' : ''}`}
                      style={{
                        background: progress.certificate.medal?.color || '#6D28D9',
                        color: '#fff',
                      }}
                    >
                      {progress.certificate.medal?.label || progress.certificate.status}
                    </span>
                  </Box>
                  <Typography variant="body2" className="tt-sans" sx={{ mb: 1.5, color: 'var(--tt-muted)' }}>
                    {progress.certificate.label}
                  </Typography>
                  <Box className="tt-sans" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: progress.certificate.speedGoalMet ? '#16A34A' : 'inherit' }}>
                      {progress.certificate.speedGoalMet ? '✓' : '○'} Speed: {progress.certificate.bestWpm}/{progress.certificate.targetWpm} WPM
                    </Typography>
                    <Typography variant="caption" sx={{ color: progress.certificate.accuracyOk ? '#16A34A' : 'inherit' }}>
                      {progress.certificate.accuracyOk ? '✓' : '○'} Accuracy ≥ 85%
                    </Typography>
                    <Typography variant="caption" sx={{ color: progress.certificate.practiceOk ? '#16A34A' : 'inherit' }}>
                      {progress.certificate.practiceOk ? '✓' : '○'} 5+ sessions
                    </Typography>
                  </Box>
                  <div className="tt-progress-track">
                    <div
                      className="tt-progress-fill"
                      style={{ width: `${progress.certificate.progressPercent || 0}%` }}
                    />
                  </div>
                </Box>
              )}

              {progress.tracks?.length > 0 && (
                <Box className="tt-card" sx={{ p: 2.5, mb: 3 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Track Status
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
                    {progress.tracks.map((track) => (
                      <Box
                        key={track.exerciseType}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: track.status === 'completed'
                            ? 'rgba(22, 163, 74, 0.12)'
                            : track.status === 'in_progress'
                              ? 'rgba(109, 40, 217, 0.1)'
                              : 'rgba(0,0,0,0.04)',
                          border: '1px solid',
                          borderColor: track.status === 'completed'
                            ? '#16A34A'
                            : track.status === 'in_progress'
                              ? '#6D28D9'
                              : 'divider',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {track.label}
                        </Typography>
                        <Typography variant="caption" className="tt-sans" display="block">
                          {track.completedLessons}/{track.totalLessons} lessons
                        </Typography>
                        <Typography variant="caption" className="tt-sans" color="text.secondary">
                          {track.sessions} sessions · {track.bestWpm} WPM best
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            color: track.status === 'completed' ? '#16A34A' : '#6D28D9',
                          }}
                        >
                          {track.status.replace('_', ' ')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              </>
            )}

            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Choose Exercise Type
            </Typography>
            <Box className="tt-exercise-grid" sx={{ mb: 4 }}>
              {EXERCISE_TYPES.map((exercise) => {
                const Icon = ICON_MAP[exercise.icon] || KeyboardIcon;
                const staticLessons = CURRICULUM[exercise.id] || [];
                const totalLessons = exercise.id === 'stories'
                  ? (progress?.lessonTotals?.stories ?? staticLessons.length)
                  : staticLessons.length;
                const doneCount = exercise.id === 'stories'
                  ? (progress?.tracks?.find((t) => t.exerciseType === 'stories')?.completedLessons
                    ?? staticLessons.filter((l) => isLessonDone(exercise.id, l.id)).length)
                  : staticLessons.filter((l) => isLessonDone(exercise.id, l.id)).length;
                return (
                  <Box
                    key={exercise.id}
                    className="tt-card tt-exercise-card"
                    onClick={() => navigate(`/student/typing-tutor/${exercise.id}`)}
                  >
                    <Icon sx={{ fontSize: 36, color: 'var(--tt-accent)', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {exercise.title}
                    </Typography>
                    <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)' }}>
                      {exercise.subtitle}
                    </Typography>
                    <Typography variant="body2" className="tt-sans" sx={{ mt: 1, fontSize: '0.85rem' }}>
                      {exercise.description}
                    </Typography>
                    <Typography variant="caption" className="tt-sans" sx={{ mt: 1, display: 'block', color: 'var(--tt-accent)' }}>
                      {doneCount}/{totalLessons} lessons practiced
                      {exercise.id === 'stories' && progress?.lmsStoryCount > 0 && (
                        <> · {progress.lmsStoryCount} from your courses</>
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Speed Games
            </Typography>
            <Box
              className="tt-card tt-exercise-card tt-games-banner"
              onClick={() => navigate('/student/typing-tutor/games')}
              sx={{
                mb: 4,
                p: 2.5,
                background: 'linear-gradient(135deg, #1a2744 0%, #2c4433 100%)',
                color: '#fff',
                border: 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SportsEsportsIcon sx={{ fontSize: 44, opacity: 0.95 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Play Speed Games
                  </Typography>
                  <Typography variant="caption" className="tt-sans" sx={{ opacity: 0.85 }}>
                    Speed Blitz · Key Pop · Type Racer
                  </Typography>
                  <Typography variant="body2" className="tt-sans" sx={{ mt: 0.75, fontSize: '0.85rem', opacity: 0.9 }}>
                    Short timed games to boost WPM. Best for students who finished home-row fingering.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className="tt-card" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Course Goals
              </Typography>
              <Typography variant="body2" className="tt-sans" sx={{ color: 'var(--tt-muted)' }}>
                DCA, Tally — target <strong>35 WPM</strong>. DTP, PGDCA — target <strong>55 WPM</strong>.
                Complete fingering first, then words, sentences, paragraph tests, and stories.
                Stories pull live notes and flash cards from your enrolled courses — type chapter content to learn and build speed.
                Use Speed Games for timed practice once home row is comfortable.
                Every session saves speed, accuracy, and time to your account — visible in the Flutter app under My Journey.
              </Typography>
              <Button
                className="tt-sans"
                variant="outlined"
                color="success"
                sx={{ mt: 2 }}
                onClick={() => navigate('/student/leaderboard')}
              >
                View LMS Leaderboard
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}
